import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface Profile {
  id: string;
  full_name: string;
  email: string;
}

interface ApprovalRequestDialogProps {
  leadId?: string;
  jobId?: string;
  orgId: string;
  children: React.ReactNode;
}

export function ApprovalRequestDialog({
  leadId,
  jobId,
  orgId,
  children,
}: ApprovalRequestDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [orgUsers, setOrgUsers] = useState<Profile[]>([]);
  const [selectedApprover, setSelectedApprover] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadOrgUsers();
    }
  }, [open, orgId]);

  const loadOrgUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("org_id", orgId)
        .neq("id", user?.id);

      if (error) throw error;
      setOrgUsers(data || []);
    } catch (error: any) {
      console.error("Error loading org users:", error);
      toast.error("Failed to load users");
    }
  };

  const handleSubmit = async () => {
    if (!selectedApprover || !user) {
      toast.error("Please select an approver");
      return;
    }

    setSubmitting(true);
    try {
      // Create approval request
      const { data: approval, error: approvalError } = await supabase
        .from("approval_requests")
        .insert({
          org_id: orgId,
          requested_by: user.id,
          approver_id: selectedApprover,
          lead_id: leadId,
          job_id: jobId,
          note: note.trim() || null,
          status: "pending",
        })
        .select()
        .single();

      if (approvalError) throw approvalError;

      // Create notification for approver
      const { error: notifError } = await supabase.from("notifications").insert({
        user_id: selectedApprover,
        org_id: orgId,
        type: "approval_request",
        title: "Approval Requested",
        message: `${user.email} is requesting your approval${
          note ? `: ${note}` : ""
        }`,
        lead_id: leadId,
        job_id: jobId,
        priority: "high",
      });

      if (notifError) console.error("Error creating notification:", notifError);

      toast.success("Approval request sent");
      setOpen(false);
      setSelectedApprover("");
      setNote("");
    } catch (error: any) {
      console.error("Error requesting approval:", error);
      toast.error("Failed to send approval request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Request Approval
          </DialogTitle>
          <DialogDescription>
            Select a team member to review and approve this {leadId ? "lead" : "job"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="approver">Select Approver</Label>
            <Select value={selectedApprover} onValueChange={setSelectedApprover}>
              <SelectTrigger id="approver">
                <SelectValue placeholder="Choose a team member" />
              </SelectTrigger>
              <SelectContent>
                {orgUsers.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.full_name} ({profile.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note (Optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any context or specific areas to review..."
              className="min-h-[100px]"
            />
          </div>

          <div className="flex items-center gap-2 p-3 bg-muted rounded-md text-sm">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              The selected user will receive a high-priority notification.
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedApprover || submitting}>
              {submitting ? "Sending..." : "Send Request"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
